import { CHESS_START_POSITION } from "../constants";
import { FenData } from "./FenData";
// For sorting and getting numeric value of a level
export const PUZZLE_COLOR_LEVELS = {
    red: 1,
    orange: 2,
    yellow: 3,
    green: 4,
    blue: 5,
    indigo: 6,
    violet: 7,
};
export const PGN_REGEX = {
    // Matches multiple standard PGN headers at the start of the file
    HEADERS_SECTION: /^(?:\[[\w\s]+\s*"[^"]*"\]\s*)+/,
    // Matches move structure for validation
    MOVES: /[1-9]\d*\./,
    // Matches a single header with capturing groups for name and value
    HEADER: /\[\s*([\w\s]+?)\s*"([^"]*)"\s*\]/g,
    // Matches specific header
    HEADER_VALUE: (header) => new RegExp(`\\[${header}\\s*"([^"]+)"\\]`),
    // Splits PGN into games, looking for Event tag
    GAME_SPLITTER: /\n+(?=\[Event\s+"[^"]*"\])/,
    // Matches FEN header
    FEN: /\[FEN "(.*?)"\]/,
    // Matches move numbers (e.g., "1." or "1...")
    MOVE_NUMBER: /^\d+\.\.?\.?$/,
    // Extracts number from move number
    MOVE_NUMBER_EXTRACT: /\d+/,
    // Matches comments
    COMMENT: /^\{[\s\S]*\}$/,
    // Matches NAG annotations
    NAG_ANNOTATION: /^\$\d+\s*$/,
    // Matches user score annotations
    USER_SCORE: /^\{\[%mus\s*-?\d*\]\}$/,
    // Extracts score from user score annotation
    USER_SCORE_EXTRACT: /%mus\s*(-?\d*)/,
    // Puzzle marker
    PUZZLE_MARKER: "{[#",
    // MCQ annotation marker
    MCQ_MARKER: "[%mcq",
    // Matches ChessBase annotations (colored squares, arrows, and legacy draw format)
    ANNOTATION: /^\{(?:\[%(?:csl|cal|draw)\s+[^\]]+\])+\}$/,
    // Matches colored squares annotation
    CSL_ANNOTATION: /\[%csl\s+([^\]]+)\]/,
    // Matches colored arrows annotation
    CAL_ANNOTATION: /\[%cal\s+([^\]]+)\]/,
    // Matches legacy draw arrow annotation
    DRAW_ANNOTATION: /\[%draw\s+([^\]]+)\]/g,
    // Matches a chess move in SAN format - includes castling moves
    MOVE_SAN: /^(O-O-O|O-O|[NBKRQ]?[a-h]?[1-8]?x?[a-h][1-8](?:=[NBRQ])?[+#]?)$/,
    // Matches game result markers
    GAME_RESULT: /^(1-0|0-1|1\/2-1\/2|\*)$/,
};
export class PgnParser {
    static REGEX = PGN_REGEX;
    /**
     * Parses a string (color name or number) into PuzzleLevel enum
     */
    static parsePuzzleLevel(value) {
        if (!value)
            return undefined;
        const colorValue = value.toLowerCase();
        return colorValue in PUZZLE_COLOR_LEVELS ? colorValue : undefined;
    }
    /**
     * Validates if a string has valid PGN structure
     */
    static isValidPgn(pgn) {
        // Check if it's a FEN position
        if (pgn.match(this.REGEX.FEN)) {
            return true;
        }
        // Check for basic PGN structure (headers and/or moves)
        const hasMoves = this.REGEX.MOVES.test(pgn);
        const hasHeaders = this.REGEX.HEADERS_SECTION.test(pgn);
        return hasMoves || hasHeaders;
    }
    /**
     * Normalizes PGN text by cleaning up whitespace and line breaks
     */
    static normalize(pgn) {
        const { headers, moves } = this.splitHeadersAndMoves(pgn);
        // Normalize headers: one header per line
        const normalizedHeaders = headers.replace(/\]\s*\[/g, "]\n[").trim();
        // Normalize moves: clean up excessive whitespace
        const normalizedMoves = moves.trim();
        return normalizedHeaders
            ? `${normalizedHeaders}\n\n${normalizedMoves}`
            : normalizedMoves;
    }
    // Header-related methods
    /**
     * Splits a PGN string into headers and moves sections
     */
    static splitHeadersAndMoves(pgn) {
        const headerMatch = pgn.match(this.REGEX.HEADERS_SECTION);
        const headers = headerMatch ? headerMatch[0].trim() : "";
        const moves = pgn.slice(headers.length).trim();
        return { headers, moves };
    }
    /**
     * Checks if PGN has malformed headers (missing closing brackets or quotes)
     */
    static hasMalformedHeaders(pgn) {
        // Remove all comments first to avoid false positives from annotations like [%draw arrow,...]
        // Use [\s\S] to match across newlines (including multiline comments)
        const pgnWithoutComments = pgn.replace(/\{[\s\S]*?\}/g, "");
        // Check for opening bracket without proper closing
        const lines = pgnWithoutComments.split("\n");
        for (const line of lines) {
            const trimmed = line.trim();
            // If line starts with [ but doesn't end with ], it's malformed
            if (trimmed.startsWith("[") && !trimmed.endsWith("]")) {
                return true;
            }
            // Check for unmatched quotes in header lines
            if (trimmed.startsWith("[")) {
                const quoteCount = (trimmed.match(/"/g) || []).length;
                // Headers should have exactly 2 quotes (opening and closing)
                if (quoteCount !== 2) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Extracts all headers from a PGN string into a Map
     */
    static extractHeaders(pgn) {
        const headers = new Map();
        const regex = new RegExp(this.REGEX.HEADER);
        let match;
        while ((match = regex.exec(pgn)) !== null) {
            const key = match[1].trim();
            const value = match[2];
            if (key) {
                headers.set(key, value);
            }
        }
        return headers;
    }
    /**
     * Gets a simplified header string without GameId, Setup, Homework, and empty values
     * @param pgn - The PGN string
     * @returns A string with simplified headers
     */
    static getSimplifiedHeaders(pgn) {
        const headers = this.extractHeaders(pgn);
        const filteredHeaders = new Map();
        // Filter out headers we don't want to show
        headers.forEach((value, key) => {
            // Skip headers with empty values, question marks, or asterisks
            if (!value || value === "" || value === "?" || value === "*") {
                return;
            }
            // Skip specific headers we don't want to show
            if (["GameId", "Setup", "Homework"].includes(key)) {
                return;
            }
            filteredHeaders.set(key, value);
        });
        // Convert to string with proper formatting
        return Array.from(filteredHeaders.entries())
            .map(([key, value]) => `[${key} "${value}"]`)
            .join("\n");
    }
    /**
     * Count the number of moves in a PGN string
     * @param pgn - The PGN string
     * @returns The number of moves
     */
    static getMoveCount(pgn) {
        if (!pgn)
            return 0;
        const { moves } = this.splitHeadersAndMoves(pgn);
        const matches = moves.match(/\b\d+\./g) || [];
        return matches.length;
    }
    /**
     * Return normalized headers text from a PGN string
     */
    static normalizeHeaders(pgn) {
        const headers = new Map();
        const existingHeaders = PgnParser.extractHeaders(pgn);
        // Ensure Event header is first
        const eventValue = existingHeaders.get("Event") || "Chess Scenario";
        headers.set("Event", eventValue);
        existingHeaders.delete("Event");
        // Add remaining headers in order (except FEN and SetUp)
        existingHeaders.forEach((value, key) => {
            if (key !== "FEN" && key !== "SetUp") {
                headers.set(key, value);
            }
        });
        // Add FEN and SetUp last if present
        const fen = existingHeaders.get("FEN");
        if (fen) {
            const fenData = new FenData(fen);
            const normalizedFen = fenData.normalizedFen || fen;
            if (normalizedFen !== CHESS_START_POSITION) {
                headers.set("FEN", normalizedFen);
                headers.set("SetUp", "1");
            }
        }
        // Convert to string with proper formatting
        return Array.from(headers.entries())
            .map(([key, value]) => `[${key} "${value}"]`)
            .join("\n");
    }
    /**
     * Gets the value of a specific header
     */
    static getHeaderValue(pgn, header) {
        const match = pgn.match(this.REGEX.HEADER_VALUE(header));
        return match ? match[1] : null;
    }
    /**
     * Extracts the initial FEN position from PGN, returns standard starting position if not found
     */
    static extractInitialFen(pgn) {
        const fenValue = PgnParser.getHeaderValue(pgn, "FEN");
        if (fenValue) {
            const fenData = new FenData(fenValue);
            return fenData.normalizedFen || CHESS_START_POSITION;
        }
        return CHESS_START_POSITION;
    }
    /**
     * Sets or updates a header value in the PGN
     */
    static setHeader(pgn, header, value) {
        const headerTag = `[${header} "${value}"]`;
        const existingHeader = this.REGEX.HEADER_VALUE(header);
        if (pgn.match(existingHeader)) {
            return pgn.replace(existingHeader, headerTag);
        }
        const { headers, moves } = this.splitHeadersAndMoves(pgn);
        return headers
            ? `${headers}\n${headerTag}\n\n${moves}`
            : `${headerTag}\n\n${moves}`;
    }
    // Move-related methods
    /**
     * Tokenizes the moves section of a PGN
     */
    static tokenizeMoves(movesText) {
        // Remove headers only from the start of the PGN
        // Use the proper header regex that handles square brackets in values
        let processedPgn = movesText.replace(this.REGEX.HEADERS_SECTION, "");
        // First, temporarily replace spaces, parentheses, line breaks, and dots in comments with special markers
        // This prevents comments containing move notation (like "1...Rd5") from being split
        processedPgn = processedPgn.replace(/\{([^}]*)\}/g, (match) => {
            return match
                .replace(/\r\n|\r|\n/g, "§NEWLINE§") // Replace all types of line breaks
                .replace(/\s/g, "§SPACE§")
                .replace(/\(/g, "§LPAREN§")
                .replace(/\)/g, "§RPAREN§")
                .replace(/\./g, "§DOT§");
        });
        return processedPgn
            .replace(/\s+/g, " ") // Normalize whitespace
            .replace(/\s*\(\s*/g, " ( ") // Normalize variation start
            .replace(/\s*\)\s*/g, " ) ") // Normalize variation end
            .replace(/(\d+\.+)([^.\s])/g, "$1 $2") // Ensure space after dot sequence
            .trim()
            .split(/\s+/)
            .map((token) => token
            .replace(/§NEWLINE§/g, "\n")
            .replace(/§SPACE§/g, " ")
            .replace(/§LPAREN§/g, "(")
            .replace(/§RPAREN§/g, ")")
            .replace(/§DOT§/g, "."));
    }
    /**
     * Determines if a PGN contains puzzle/interactive content.
     * A PGN is a puzzle if it has:
     * - Puzzle markers {[#]}
     * - MCQ annotations {[%mcq ...]}
     * - MiniGame header [MiniGame "..."]
     *
     * @param pgn - The PGN string to check
     * @returns true if the PGN contains puzzle/interactive annotations
     */
    static isPuzzle(pgn) {
        return (pgn.includes(this.REGEX.PUZZLE_MARKER) ||
            pgn.includes(this.REGEX.MCQ_MARKER) ||
            this.hasMiniGameHeader(pgn));
    }
    /**
     * Checks if a PGN has a MiniGame header.
     */
    static hasMiniGameHeader(pgn) {
        if (!pgn)
            return false;
        return this.REGEX.HEADER_VALUE("MiniGame").test(pgn);
    }
    // Game-related methods
    /**
     * Splits a PGN string into multiple games
     */
    static splitGames(pgn) {
        return pgn
            .split(this.REGEX.GAME_SPLITTER)
            .map((game) => game.trim())
            .filter((game) => game);
    }
}
